/**
 * The Monitor's inbox work as its views show it (PRD-0075 R-B5, R-B6): an alert's triage and
 * whether the reader has seen it, the standing sources as rows, the investigations an alert can go
 * into. Pure, so the page and the stories map API fixtures the same way.
 */
import type { InvestigationChoice } from '@/components/monitor/AcceptAlertDialog'
import type { AlertInboxRow } from '@/components/monitor/AlertInbox'
import {
  ALERT_STATES,
  type AlertTriageRow,
  SNOOZE_DAYS,
  type SnoozeLength,
} from '@/components/monitor/AlertTriage'
import { SOURCE_CHANNELS, type StandingSourceRow } from '@/components/monitor/SourcesPane'
import type { AlertDto, AlertState, StandingSourceDto } from '@/services/api/case-monitor'
import type { Investigation } from '@/types/investigation'
import { toInboxRows } from './monitor-rows'

const DAY_MS = 24 * 60 * 60 * 1000

function alertState(value: string): AlertState | null {
  return ALERT_STATES.find(state => state === value) ?? null
}

/** What the team did with the alert, as its row says it. */
export function triageOf(alert: AlertDto): AlertTriageRow {
  return {
    state: alertState(alert.state),
    byName: alert.triaged_by_name ?? null,
    at: alert.triaged_at ?? null,
    snoozedUntil: alert.snoozed_until ?? null,
    investigationId: alert.investigation_id ?? null,
    investigationTitle: alert.investigation_title ?? null,
  }
}

/**
 * Whether the reader has not seen the alert: raised after their visit before this one. While the
 * visit is not marked yet (`undefined`) nothing is flagged; on a first visit (`null`) everything is.
 */
export function isUnread(alert: AlertDto, unreadSince: string | null | undefined): boolean {
  if (unreadSince === undefined) return false
  return unreadSince === null || Date.parse(alert.created_at) > Date.parse(unreadSince)
}

/** The inbox rows with their triage and whether the reader has seen them. */
export function toTriagedRows(
  alerts: readonly AlertDto[],
  unreadSince: string | null | undefined
): AlertInboxRow[] {
  return toInboxRows(alerts).map((row, index) => ({
    ...row,
    triage: triageOf(alerts[index]),
    isUnread: isUnread(alerts[index], unreadSince),
  }))
}

/** The newest alert's raise time: a change while the page is open marks the visit again. */
export function newestRaised(alerts: readonly AlertDto[] | undefined): string | undefined {
  return alerts?.reduce<string | undefined>(
    (newest, alert) =>
      newest === undefined || alert.created_at > newest ? alert.created_at : newest,
    undefined
  )
}

export function sourceRows(sources: readonly StandingSourceDto[]): StandingSourceRow[] {
  return sources.map(source => ({
    key: `${source.channel}:${source.folder}`,
    channel: SOURCE_CHANNELS.find(channel => channel === source.channel) ?? null,
    folder: source.folder,
    documents: source.documents,
    lastReceivedAt: source.last_received_at,
    lastDocumentId: source.last_document_id,
    lastDocumentName: source.last_document_name,
  }))
}

export function investigationChoices(
  investigations: readonly Pick<Investigation, 'id' | 'title' | 'readOnly'>[]
): InvestigationChoice[] {
  return investigations.map(investigation => ({
    id: investigation.id,
    title: investigation.title,
    readOnly: investigation.readOnly,
  }))
}

/** When a snooze of ``length`` that starts at ``now`` ends; ISO 8601. */
export function snoozeUntil(now: Date, length: SnoozeLength): string {
  return new Date(now.getTime() + SNOOZE_DAYS[length] * DAY_MS).toISOString()
}
